import data from './listingItemTypes.json';

/**
 * What a listing rents out — the frontend mirror of App\Models\Enums\ListingItemType.
 * The data lives in listingItemTypes.json, which tests/Unit/Enums/ListingItemTypeTest.php
 * checks against the PHP enum. See docs/non-vehicle-listings.md.
 */
export type ListingItemType = 'vehicle' | 'trailer' | 'golf_cart' | 'powersports' | 'watercraft' | 'equipment';

export interface ListingItemTypeCapabilities {
    label: string;
    noun: string;
    requiresYearMakeModel: boolean;
    hasSeats: boolean;
    hasDoors: boolean;
    hasPlate: boolean;
    tracksDistance: boolean;
    needsDriverLicense: boolean;
    allowsSecondaryDriver: boolean;
    needsInsuranceVerification: boolean;
    supportsCarIntegrations: boolean;
}

/** A shortcut on the "Something else" picker: sets a type and a friendly name. */
export interface ListingQuickPick {
    key: string;
    label: string;
    type: ListingItemType;
    itemLabel: string | null;
    /** "Other…": the host types their own name. */
    custom?: boolean;
}

export const LISTING_ITEM_TYPES = data.types as Record<ListingItemType, ListingItemTypeCapabilities>;

export const LISTING_QUICK_PICKS = data.quickPicks as ListingQuickPick[];

export const DEFAULT_LISTING_ITEM_TYPE: ListingItemType = 'vehicle';

/** Capabilities for a listing's type; anything missing or unknown is a vehicle, as before. */
export function listingItemType(listing: { item_type?: string | null } | null | undefined): ListingItemTypeCapabilities {
    const type = listing?.item_type as ListingItemType | undefined;

    return (type && LISTING_ITEM_TYPES[type]) || LISTING_ITEM_TYPES[DEFAULT_LISTING_ITEM_TYPE];
}

/**
 * What to call a listing's item: the host's own name ("Horse trailer") when
 * set, else the type's label ("Trailer").
 */
export function listingItemLabel(listing: { item_type?: string | null; item_label?: string | null } | null | undefined): string {
    const custom = listing?.item_label?.trim();

    return custom || listingItemType(listing).label;
}

export const VEHICLE_PLACEHOLDER_IMAGE = '/images/vehicle-placeholder.jpg';
export const LISTING_PLACEHOLDER_IMAGE = '/images/listing-placeholder.svg';

/** The stand-in photo for a listing without one: a car only for cars. */
export function listingPlaceholderImage(listing: { item_type?: string | null } | null | undefined): string {
    return (listing?.item_type ?? 'vehicle') === 'vehicle' ? VEHICLE_PLACEHOLDER_IMAGE : LISTING_PLACEHOLDER_IMAGE;
}
